import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import api from '../api';
import { Search, Eye, ChevronUp, ChevronDown, X } from 'lucide-react';
import Pagination from '../components/Pagination';

const PaymentManagement = () => {
  const { instance, accounts } = useMsal();
  const [payments, setPayments] = useState([]);
  const [approvedExpenses, setApprovedExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expenseType, setExpenseType] = useState('all');
  const [sortDirection, setSortDirection] = useState('asc'); // asc | desc
  const [userRole, setUserRole] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [uploadForm, setUploadForm] = useState({
    expenseId: '',
    vendorId: '',
    files: [],
    remarks: '',
    paymentType: 'full'
  });
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [isExpenseListOpen, setIsExpenseListOpen] = useState(false);

  const userRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const hasRole = (role) => userRoles.includes(role);

  const isPaymentTeam = hasRole('payment') || hasRole('admin');
  const allowedRoles = ['payment', 'admin', 'approver', 'creator'];
  const isViewOnly = !hasRole('admin') && !hasRole('payment') && (hasRole('approver') || hasRole('creator'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0]
        });


        // Fetch user role
        const userInfo = await api.get(`/api/users/${accounts[0].username}`, {
          headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
        });

        const currentUserRole = userInfo.data?.role || 'creator';
        setUserRole(currentUserRole);

        const currentUserRoles = currentUserRole.split(',').map(r => r.trim());
        const isAllowed = currentUserRoles.some(r => allowedRoles.includes(r));
        if (!isAllowed) {
          setLoading(false);
          return;
        }

        // Fetch payments and approved expenses (for all allowed roles)
        const paymentReqOptions = {
          headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
        };
        if (statusFilter !== 'all') {
          paymentReqOptions.params = { status: statusFilter };
        }

        const [paymentsRes, approvedExpensesRes, sentToPaymentRes] = await Promise.all([
          api.get('/api/payments', paymentReqOptions),
          api.get('/api/expenses/approved', {
            headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
          }),
          api.get('/api/payments/sent-to-payment', {
            headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
          })
        ]);

        let paymentRows = paymentsRes.data || [];

        // Fallback for approver: if no receipts yet, show items sent to payment (selected vendors)
        const isApprover = currentUserRoles.includes('approver');
        if (isApprover && paymentRows.length === 0) {
          try {
            const sentToPaymentRes = await api.get('/api/payments/sent-to-payment', {
              headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
            });
            const fallbackRows = (sentToPaymentRes.data || []).map(r => ({
              expense_id: r.expense_id,
              vendor_name: r.vendor_name,
              notes: '',
              status: 'Pending',
              receipt_url: null,
              created_at: r.created_at
            }));
            // Do not include fallback rows in Payment History; list must only contain uploaded receipts
            paymentRows = (paymentRows || []).filter(r => r && r.receipt_url);
          } catch (e) {
            console.warn('Approver fallback list fetch failed:', e);
          }
        }

        // Ensure list shows only entries with actual receipts (exclude synthetic/no-receipt rows)
        try {
          paymentRows = (paymentRows || []).filter(r => r && r.receipt_url);
        } catch (mergeErr) {
          console.warn('Merge of approved-without-receipt rows failed:', mergeErr);
        }

        setPayments(paymentRows);

        // Show ALL approved expenses in dropdown (even if a payment exists),
        // since payment team may need to re-upload or manage receipts.
        // Merge approved + sent-to-payment (unique by expense_id) for dropdown
        const approvedRaw = Array.isArray(approvedExpensesRes.data) ? approvedExpensesRes.data : [];
        const sentList = Array.isArray(sentToPaymentRes.data) ? sentToPaymentRes.data : [];
        const unionMap = new Map();
        for (const exp of approvedRaw) {
          const id = String(exp.expense_id || '');
          if (!id) continue;
          unionMap.set(id, {
            expense_id: exp.expense_id,
            expense_name: exp.expense_name || 'Unnamed Expense',
            vendor_name: exp.vendor_name || 'Vendor not specified',
            cost: exp.cost || 0
          });
        }
        for (const sent of sentList) {
          const id = String(sent.expense_id || '');
          if (!id) continue;
          if (!unionMap.has(id)) {
            unionMap.set(id, {
              expense_id: sent.expense_id,
              expense_name: sent.expense_name || 'Unnamed Expense',
              vendor_name: sent.vendor_name || 'Vendor not specified',
              cost: sent.cost || 0
            });
          }
        }
        let mappedApproved = Array.from(unionMap.values());

        // Backfill missing expense_name by querying expenses table
        try {
          const needsName = mappedApproved.filter(e => !e.expense_name || e.expense_name === 'Unnamed Expense');
          if (needsName.length > 0) {
            const nameFetches = needsName.map(async (e) => {
              try {
                const info = await api.get('/api/expenses/by', {
                  params: { expenseId: e.expense_id },
                  headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
                });
                const nm = info?.data?.expense_name;
                return { id: e.expense_id, name: nm };
              } catch (_) {
                return { id: e.expense_id, name: null };
              }
            });
            const filled = await Promise.all(nameFetches);
            const nameMap = new Map(filled.filter(f => f.name).map(f => [String(f.id), f.name]));
            mappedApproved = mappedApproved.map(e => (
              nameMap.has(String(e.expense_id))
                ? { ...e, expense_name: nameMap.get(String(e.expense_id)) }
                : e
            ));
          }
        } catch (e) {
          console.warn('Failed to backfill expense names for dropdown:', e);
        }

        // Exclude any expenses that already have a receipt in Payment History
        try {
          const paidIds = new Set((paymentRows || [])
            .filter(r => r && r.receipt_url)
            .map(r => String(r.expense_id || '')));
          mappedApproved = mappedApproved.filter(e => !paidIds.has(String(e.expense_id || '')));
        } catch (_) { }

        setApprovedExpenses(mappedApproved);

        if (mappedApproved.length > 0) {
          if (!uploadForm.expenseId || !mappedApproved.some(e => String(e.expense_id) === String(uploadForm.expenseId))) {
            setUploadForm(prev => ({
              ...prev,
              expenseId: mappedApproved[0].expense_id // Set first available expense as default
            }));
          }
        } else {
          // No selectable expenses; clear selection
          setUploadForm(prev => ({ ...prev, expenseId: '', vendorId: '' }));
          setSelectedVendors([]);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (accounts.length > 0) {
      fetchData();
    }
  }, [accounts, statusFilter, instance, uploadForm.expenseId]);

  // Load selected vendors whenever expense changes (for per-vendor receipt upload)
  useEffect(() => {
    const loadVendors = async () => {
      if (!uploadForm.expenseId) { setSelectedVendors([]); return; }
      try {
        const tokenResponse = await instance.acquireTokenSilent({ scopes: ["User.Read"], account: accounts[0] });
        const encodedId = encodeURIComponent(encodeURIComponent(uploadForm.expenseId));
        const res = await api.get(`/api/payments/selected-vendors/${encodedId}`, {
          headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
        });
        setSelectedVendors(res.data || []);
        if ((res.data || []).length > 0) {
          setUploadForm(prev => ({ ...prev, vendorId: String(res.data[0].id) }));
        } else {
          setUploadForm(prev => ({ ...prev, vendorId: '' }));
        }
      } catch (e) {
        console.error('Failed to load selected vendors:', e);
        setSelectedVendors([]);
        setUploadForm(prev => ({ ...prev, vendorId: '' }));
      }
    };
    if (accounts.length > 0) loadVendors();
  }, [uploadForm.expenseId, accounts, instance]);

  // Debug effect to log approved expenses (optional, can be removed)
  useEffect(() => {
    const fetchApprovedExpenses = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: accounts[0]
        });

        const response = await api.get('/api/expenses/approved', {
          headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
        });
      } catch (error) {
        console.error('Error fetching approved expenses:', error);
      }
    };

    if (accounts.length > 0) {
      // fetchApprovedExpenses(); // Commented out to reduce console noise
    }
  }, [accounts, approvedExpenses, instance]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!uploadForm.expenseId || !uploadForm.vendorId || !uploadForm.files || uploadForm.files.length === 0) return;

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0]
      });

      const encodedId = encodeURIComponent(encodeURIComponent(uploadForm.expenseId));
      // Upload each file separately so backend (single file handler) can process
      for (const file of Array.from(uploadForm.files)) {
        const formData = new FormData();
        formData.append('receipt', file);
        formData.append('vendorId', uploadForm.vendorId);
        if (uploadForm.remarks) formData.append('remarks', uploadForm.remarks);
        // Include selected payment type; backend may ignore if unsupported
        formData.append('paymentType', uploadForm.paymentType);

        await api.post(
          `/api/payments/${encodedId}/receipt`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${tokenResponse.accessToken}`
            }
          }
        );
      }

      // Reset form (keep same expense selected, reset files & remarks)
      setUploadForm(prev => ({
        ...prev,
        vendorId: selectedVendors[0]?.id ? String(selectedVendors[0].id) : '',
        files: [],
        remarks: ''
      }));
      const fileEl = document.getElementById('receipt-file');
      if (fileEl) fileEl.value = '';

      // Refresh page data
      window.location.reload();

    } catch (err) {
      console.error('Error uploading receipt:', err);
      alert(`Failed to upload receipt: ${err.response?.data?.error || err.message}`);
    }
  };

  // Update payment status (used by Approve/Reject buttons)
  const handleStatusUpdate = async (paymentId, nextStatus) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: accounts[0]
      });
      await api.patch(
        `/api/payments/${encodeURIComponent(paymentId)}/status`,
        { status: nextStatus },
        { headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` } }
      );
      // Refresh list without full reload
      const refreshOptions = {
        headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
      };
      if (statusFilter !== 'all') {
        refreshOptions.params = { status: statusFilter };
      }
      const refreshed = await api.get('/api/payments', refreshOptions);
      setPayments(refreshed.data || []);
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert(`Failed to update status: ${err.response?.data?.error || err.message}`);
    }
  };

  // New filtered list for the dropdown
  const filteredApprovedExpenses = approvedExpenses.filter(expense => {
    if (!expenseSearchTerm) return true;
    const term = expenseSearchTerm.toLowerCase();
    return (
      String(expense.expense_id || '').toLowerCase().includes(term) ||
      String(expense.expense_name || '').toLowerCase().includes(term)
    );
  });

  // Custom select handler for the new dropdown structure
  const handleExpenseSelect = (expenseId) => {
    setUploadForm(prev => ({ ...prev, expenseId }));
    setIsExpenseListOpen(false); // Close dropdown after selection
    setExpenseSearchTerm(''); // Clear search term after selection
  };

  // Find the currently selected expense object to display its name in the input
  const selectedExpense = approvedExpenses.find(e => String(e.expense_id) === String(uploadForm.expenseId));
  const selectedExpenseDisplay = selectedExpense
    ? `${selectedExpense.expense_id} - ${selectedExpense.expense_name}`
    : (approvedExpenses.length === 0 ? 'No approved expenses available' : 'Select an Expense...');


  // Existing logic for Payment History table filtering
  const filteredPayments = payments
    .filter(payment => {
      const matchesSearch = payment.expense_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
      const matchesType = expenseType === 'all'
        ? true
        : (expenseType === 'IT'
          ? (payment.expense_id && payment.expense_id.includes('/IT/'))
          : (expenseType === 'Accounts'
            ? (payment.expense_id && payment.expense_id.includes('/ACCOUNT/'))
            : (!payment.expense_id?.includes('/IT/') && !payment.expense_id?.includes('/ACCOUNT/'))));
      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a, b) => {
      const av = String(a.expense_id || '');
      const bv = String(b.expense_id || '');
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination derived from filtered list
  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / itemsPerPage));
  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleSort = () => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  // Explicit guard: only payment/admin should access this page
  const currentUserRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const isAllowed = currentUserRoles.some(r => allowedRoles.includes(r));
  if (!isAllowed) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <h2 className="text-xl font-semibold text-gray-800">Access denied</h2>
        <p className="text-gray-500 mt-2">You do not have permission to view Payment Management.</p>
      </div>
    );
  }

  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--heading-color, var(--text))' }}>Payment Management</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Manage payments, upload receipts, and track payment transactions</p>
      </div>

      {/* View-only notice */}
      {isViewOnly && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800 text-sm">
          View-only: Approvers & Creators can see the list but cannot upload or modify.
        </div>
      )}
      {/* Upload Form (hidden in view-only) */}
      {!isViewOnly && (
        <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text)' }}>Upload Receipt</h2>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Left column: inputs */}
              <div className="md:col-span-2 space-y-4">
                {/* Expense Searchable Dropdown (FIXED ICONS & FONT) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expense
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={selectedExpenseDisplay}
                      value={isExpenseListOpen ? expenseSearchTerm : selectedExpenseDisplay}
                      onChange={(e) => {
                        setExpenseSearchTerm(e.target.value);
                        setIsExpenseListOpen(true);
                      }}
                      onFocus={() => setIsExpenseListOpen(true)}
                      // FIX: pr-20 for space (pr-16 -> pr-20)
                      className="w-full pl-10 pr-20 py-2 rounded-md border text-sm"
                      style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      disabled={!approvedExpenses.length}
                    />

                    {/* Search Icon (inside the input) - positioned left */}
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                    />

                    {/* Clear Search Term Icon (X) - positioned right, closer to the center */}
                    {expenseSearchTerm && (
                      <X
                        // FIX: Adjusted position to right-12 to clear the dropdown icon
                        className="absolute right-12 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 z-20"
                        onClick={() => setExpenseSearchTerm('')}
                        title="Clear Search"
                      />
                    )}

                    {/* Dropdown Icon - positioned far right */}
                    <ChevronDown
                      // Adjusted position to right-4
                      className={`absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer transition-transform duration-200 ${isExpenseListOpen ? 'transform rotate-180' : ''}`}
                      onClick={() => setIsExpenseListOpen(prev => !prev)}
                    />

                    {/* Dropdown List (FONT STYLE RESTORED) */}
                    {isExpenseListOpen && (
                      <div
                        className="absolute z-10 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto border"
                        style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}
                      >
                        {filteredApprovedExpenses.length === 0 ? (
                          <div className="p-2 text-sm text-gray-500">
                            No matching expenses found.
                          </div>
                        ) : (
                          filteredApprovedExpenses.map((expense, index) => (
                            <div
                              key={`expense-search-${expense.expense_id}-${index}`}
                              onClick={() => handleExpenseSelect(expense.expense_id)}
                              // FIX: Added text-sm for font size consistency
                              className={`p-2 cursor-pointer hover:bg-gray-100 text-sm ${String(expense.expense_id) === String(uploadForm.expenseId) ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                              style={{
                                color: 'var(--text)',
                                backgroundColor: String(expense.expense_id) === String(uploadForm.expenseId) ? 'rgba(59,130,246,0.1)' : 'var(--panel)',
                                borderColor: 'var(--border)'
                              }}
                            >
                              <span className="font-semibold">{expense.expense_id}</span> - {expense.expense_name}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Vendor selection for per-vendor receipts */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor (for receipt)
                  </label>
                  <select
                    name="vendorId"
                    value={uploadForm.vendorId}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, vendorId: e.target.value }))}
                    className="w-full p-2 rounded-md border"
                    style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                    required
                    disabled={!selectedVendors.length}
                  >
                    {selectedVendors.length === 0 ? (
                      <option value="">No selected vendors for this expense</option>
                    ) : (
                      selectedVendors.map(v => (
                        <option key={`vendor-${v.id}`} value={v.id}>{v.vendor_name} — ₹{v.cost}</option>
                      ))
                    )}
                  </select>
                </div>
                {/* Payment Type selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Type
                  </label>
                  <div className="flex items-center gap-6">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="paymentType"
                        value="full"
                        checked={uploadForm.paymentType === 'full'}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, paymentType: e.target.value }))}
                        className="h-4 w-4 text-blue-600 border-gray-300"
                      />
                      Full Payment
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="paymentType"
                        value="partial"
                        checked={uploadForm.paymentType === 'partial'}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, paymentType: e.target.value }))}
                        className="h-4 w-4 text-blue-600 border-gray-300"
                      />
                      Partial Payment
                    </label>
                  </div>
                </div>
                {/* Receipt File(s) */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Receipt File(s)
                    </label>
                    <span className="text-xs text-gray-400">PDF, JPG, PNG</span>
                  </div>
                  <input
                    type="file"
                    id="receipt-file"
                    onChange={(e) => setUploadForm(prev => ({ ...prev, files: e.target.files }))}
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    className="block w-full text-sm"
                    style={{ color: 'var(--text)' }}
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Max 10MB.</p>
                </div>
                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remarks (optional)
                  </label>
                  <textarea
                    name="remarks"
                    rows={2}
                    maxLength={250}
                    value={uploadForm.remarks}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Any notes about this receipt..."
                    className="w-full p-2 rounded-md resize-none border"
                    style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Up to 250 characters</span>
                    <span>{uploadForm.remarks?.length || 0}/250</span>
                  </div>
                </div>
              </div>

              {/* Right column: summary + submit */}
              <div className="md:col-span-1">
                <div className="h-full flex flex-col justify-between rounded-lg p-4 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
                  <div>
                    <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>Ready to upload?</h3>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Review the details and then submit.</p>
                    <ul className="mt-3 text-xs space-y-1" style={{ color: 'var(--text)' }}>
                      <li><span className="font-medium">Expense:</span> {selectedExpenseDisplay}</li>
                      <li><span className="font-medium">Vendor:</span> {selectedVendors.find(v => String(v.id) === String(uploadForm.vendorId))?.vendor_name || 'Not selected'}</li>
                      <li><span className="font-medium">Files:</span> {uploadForm.files && uploadForm.files.length > 0 ? `${uploadForm.files.length} file(s)` : 'No files chosen'}</li>
                      <li><span className="font-medium">Payment Type:</span> {uploadForm.paymentType === 'partial' ? 'Partial' : 'Full'}</li>
                      <li><span className="font-medium">Remarks:</span> {uploadForm.remarks ? uploadForm.remarks : '—'}</li>
                    </ul>
                  </div>
                  <button
                    type="submit"
                    className="mt-4 w-full py-2 px-4 rounded-md border font-semibold"
                    style={{ background: '#8c211e', color: '#fff', borderColor: '#721a18' }}
                    disabled={!uploadForm.expenseId || !uploadForm.vendorId || !uploadForm.files || uploadForm.files.length === 0}
                  >
                    Upload Receipt
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Payment List */}
      <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Payment History</h2>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>Manage receipts and payment actions</p>
          </div>
          <div className="flex space-x-3">
            {/* Search Input with Clear Button (X) - FIXED ICON VISIBILITY */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by expense ID or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                // FIX: Increased pr-9 to pr-12 to make room for X icon
                className="pl-10 pr-12 py-2 rounded-lg border"
                style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
              />
              {/* Clear Search Term Icon */}
              {searchTerm && (
                <X
                  // right-3 is fine here, now that the input has pr-12
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                  onClick={() => setSearchTerm('')}
                  title="Clear Search"
                />
              )}
            </div>
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg px-4 py-2 border"
              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            >
              <option value="all">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Paid">Paid</option>
            </select>
            {/* Expense Type Filter */}
            <select
              value={expenseType}
              onChange={(e) => setExpenseType(e.target.value)}
              className="rounded-lg px-4 py-2 border"
              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            >
              <option value="all">All Types</option>
              <option value="IT">IT Expenses</option>
              <option value="Accounts">Accounts Expenses</option>
              <option value="Admin">Admin Expenses</option>
            </select>
          </div>
        </div>

        {/* Payment Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full" style={{ color: 'var(--text)' }}>
            <thead className="border-b" style={{ borderColor: 'var(--border)', background: 'var(--panel)', color: 'var(--muted)' }}>
              <tr>
                <th className="px-4 py-3 text-left text-md font-medium tracking-wider cursor-pointer" onClick={toggleSort} title="Sort by Expense ID">
                  <div className="flex items-center gap-1">
                    Expense ID
                    {sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-md font-medium tracking-wider">Vendor</th>
                <th className="px-4 py-3 text-left text-md font-medium tracking-wider">Payment Type</th>
                <th className="px-4 py-3 text-left text-md font-medium tracking-wider">Remarks</th>
                <th className="px-4 py-3 text-left text-md font-medium tracking-wider">View Receipt</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayments.length > 0 ? (
                paginatedPayments.map((payment, index) => (
                  <tr key={`payment-${payment.payment_id ?? `${payment.expense_id || 'unknown'}-${payment.created_at || index}`}`}>
                    <td className="px-4 py-3 whitespace-nowrap font-medium">{payment.expense_id}</td>
                    <td className="px-4 py-3 whitespace-nowrap" style={{ color: 'var(--text)' }}>{payment.vendor_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap" style={{ color: 'var(--text)' }}>{payment.payment_type ? (payment.payment_type === 'partial' || payment.payment_type === 'Partial' ? 'Partial' : 'Full') : '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap max-w-xs truncate" title={payment.notes || ''} style={{ color: 'var(--text)' }}>
                      {payment.notes ? payment.notes : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        {payment.receipt_url ? (
                          <a
                            href={payment.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 rounded-full transition"
                            style={{ background: 'rgba(59,130,246,0.18)', color: '#1d4ed8' }}
                          >
                            <Eye className="h-4 w-4" aria-hidden="true" />
                            <span className="sr-only">View Receipt</span>
                          </a>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-full" title="No receipt uploaded" style={{ background: 'rgba(148,163,184,0.16)', color: 'var(--muted)' }}>No Receipt</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center" style={{ color: 'var(--muted)' }}>
                    No payments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filteredPayments.length > 0 && (
          <div className="border-t" style={{ borderColor: 'var(--border)' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setCurrentPage(p)}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              totalItems={filteredPayments.length}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentManagement;
